import { Card, CardTitle } from "@/components/Card";
import { cn } from "@/lib/cn";
import type {
  GraphEdge,
  LegacyTransitionRuleDocument,
  TransitionRuleDocument,
} from "@/lib/types";
import { conditionNames, predicateFields } from "@/lib/phaseVocabulary";
import {
  RowField,
  RowListField,
  RowTextInput,
  SelectField,
} from "@/components/strategy/FieldEditors";
import PredicateEditor from "@/components/strategy/PredicateEditor";
import {
  PROSPECT_STATUSES,
  phaseOptions,
  usePhaseEditor,
  type PhaseEditorProps,
} from "@/components/strategy/phaseEditing";

/**
 * Both rule shapes the engine evaluates for a phase, editable in place:
 *
 * - priority rules (`transitions`) with a structured `when` condition;
 * - classifier rules (`transition_rules`), the flat shape whose `condition`
 *   name the reply classifier is told to detect.
 *
 * Shown in evaluation order: higher priority first, classifier rules at
 * priority 0 in declaration order — the order the engine actually uses.
 */
export default function TransitionsEditor({
  outgoing,
  incoming,
  ...props
}: PhaseEditorProps & { outgoing: GraphEdge[]; incoming: GraphEdge[] }) {
  const { phase, list, edit, isEdited } = usePhaseEditor(props);
  const rules = list<TransitionRuleDocument>(["transitions"]) ?? [];
  const legacy = list<LegacyTransitionRuleDocument>(["transition_rules"]);
  const phases = phaseOptions(props.document);
  const statuses = PROSPECT_STATUSES.map((s) => ({ value: s, label: s }));
  const fields = predicateFields(props.document);
  const names = conditionNames(phase);

  const commitRules = (next: TransitionRuleDocument[]) =>
    edit(["transitions"], next.length === 0 ? null : next);
  const updateRule = (i: number, patch: (r: TransitionRuleDocument) => TransitionRuleDocument) =>
    commitRules(rules.map((r, j) => (j === i ? patch(structuredClone(r)) : r)));

  return (
    <Card>
      <div className="flex items-center gap-1.5">
        <CardTitle>Transitions</CardTitle>
        {isEdited(["transitions"]) || isEdited(["transition_rules"]) ? (
          <span title="unsaved change" className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
        ) : null}
      </div>
      <p className="mb-2 text-xs text-[var(--fg-muted)]">
        Evaluated top to bottom after every reply and every send; the first rule whose
        condition holds moves the prospect. Priority rules run before classifier rules.
      </p>

      <div className="mb-1 text-xs font-medium">Priority rules</div>
      {rules.length === 0 ? (
        <p className="mb-2 text-xs text-[var(--fg-muted)]">none</p>
      ) : (
        <ul className="space-y-2">
          {[...rules.keys()]
            .sort((a, b) => (rules[b]?.priority ?? 0) - (rules[a]?.priority ?? 0) || a - b)
            .map((i) => {
              const rule = rules[i]!;
              return (
                <li key={i} className="rounded border border-[var(--border)] p-2 text-xs space-y-1.5">
                  <div className="flex items-center gap-2">
                    <input
                      aria-label={`Rule ${i + 1} name`}
                      className="min-w-0 flex-1 rounded border border-[var(--border)] bg-[var(--bg-card)] px-1.5 py-0.5"
                      value={rule.name ?? ""}
                      placeholder="rule name"
                      onChange={(e) =>
                        updateRule(i, (r) => ({ ...r, name: e.target.value || undefined }))
                      }
                    />
                    <label className="flex items-center gap-1 text-[var(--fg-muted)]">
                      priority
                      <input
                        aria-label={`Rule ${i + 1} priority`}
                        type="number"
                        className="w-14 rounded border border-[var(--border)] bg-[var(--bg-card)] px-1 py-0.5 tabular-nums"
                        value={rule.priority ?? 0}
                        onChange={(e) =>
                          updateRule(i, (r) => ({ ...r, priority: Number(e.target.value) }))
                        }
                      />
                    </label>
                    <button
                      type="button"
                      aria-label={`Remove rule ${i + 1}`}
                      className="text-[var(--fg-muted)] hover:text-[var(--danger)]"
                      onClick={() => commitRules(rules.filter((_, j) => j !== i))}
                    >
                      ×
                    </button>
                  </div>

                  <ThenFields
                    label={`Rule ${i + 1}`}
                    nextPhase={rule.then?.next_phase ?? null}
                    setStatus={rule.then?.set_status ?? null}
                    requiresApproval={rule.then?.requires_human_approval === true}
                    phases={phases}
                    statuses={statuses}
                    onChange={(then) =>
                      updateRule(i, (r) => ({ ...r, then: { ...(r.then ?? {}), ...then } }))
                    }
                  />

                  <div>
                    <div className="mb-0.5 text-[var(--fg-muted)]">when</div>
                    <PredicateEditor
                      label={`Rule ${i + 1}`}
                      value={rule.when ?? null}
                      fields={fields}
                      conditionNames={names}
                      onChange={(when) =>
                        updateRule(i, (r) => {
                          const next = { ...r };
                          if (when === null) delete next.when;
                          else next.when = when;
                          return next;
                        })
                      }
                    />
                  </div>
                </li>
              );
            })}
        </ul>
      )}
      <button
        type="button"
        className="mt-2 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-zinc-50"
        onClick={() =>
          commitRules([...rules, { name: `rule_${rules.length + 1}`, priority: 0, then: {} }])
        }
      >
        Add priority rule
      </button>

      <div className="mt-4 mb-1 text-xs font-medium">Classifier rules</div>
      <p className="mb-2 text-xs text-[var(--fg-muted)]">
        Each condition is a name the reply classifier is asked to detect in the prospect's
        reply, so describe the situation in the name itself. The description is shown on the
        flowchart.
      </p>
      <RowListField<LegacyTransitionRuleDocument>
        value={legacy}
        rowLabel="classifier rule"
        addLabel="Add classifier rule"
        emptyText="none"
        newRow={() => ({ condition: "", next_phase: phases[0]?.value ?? "" })}
        onCommit={(rows) => edit(["transition_rules"], rows)}
        renderRow={(row, update, i) => (
          <>
            <div className="grid grid-cols-[1fr_1.5fr] gap-2">
              <RowField label="condition">
                <RowTextInput
                  ariaLabel={`Classifier rule ${i + 1} condition`}
                  value={row.condition}
                  mono
                  placeholder="e.g. asks_for_pricing"
                  onCommit={(v) => update({ ...row, condition: v.trim() })}
                />
              </RowField>
              <RowField label="description">
                <RowTextInput
                  ariaLabel={`Classifier rule ${i + 1} description`}
                  value={row.description}
                  placeholder="what the reply looks like"
                  onCommit={(v) => {
                    const next = { ...row };
                    if (v.trim() === "") delete next.description;
                    else next.description = v;
                    update(next);
                  }}
                />
              </RowField>
            </div>
            <ThenFields
              label={`Classifier rule ${i + 1}`}
              nextPhase={row.next_phase ?? null}
              setStatus={row.set_status ?? null}
              requiresApproval={row.requires_human_approval === true}
              phases={phases}
              statuses={statuses}
              onChange={(then) => {
                const next = { ...row };
                if ("next_phase" in then) next.next_phase = then.next_phase;
                if ("set_status" in then) {
                  if (then.set_status === undefined) delete next.set_status;
                  else next.set_status = then.set_status;
                }
                if ("requires_human_approval" in then) {
                  if (then.requires_human_approval) next.requires_human_approval = true;
                  else delete next.requires_human_approval;
                }
                update(next);
              }}
            />
          </>
        )}
      />

      <div className="mt-3 border-t border-[var(--border)] pt-2">
        <TransitionList title="Arrives here from" edges={incoming} direction="in" />
        {outgoing.some((e) => e.dangling) ? (
          <p className="mt-1 text-xs text-red-700">
            One or more rules point at a phase that does not exist.
          </p>
        ) : null}
      </div>
    </Card>
  );
}

/** The action half shared by both rule shapes: target, status, approval. */
function ThenFields({
  label,
  nextPhase,
  setStatus,
  requiresApproval,
  phases,
  statuses,
  onChange,
}: {
  label: string;
  nextPhase: string | null;
  setStatus: string | null;
  requiresApproval: boolean;
  phases: { value: string; label: string }[];
  statuses: { value: string; label: string }[];
  onChange: (then: {
    next_phase?: string;
    set_status?: string;
    requires_human_approval?: boolean;
  }) => void;
}) {
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[var(--fg-muted)]">next phase</span>
          <SelectField
            ariaLabel={`${label} next phase`}
            value={nextPhase}
            options={phases}
            allowEmpty="(choose)"
            onCommit={(v) => onChange({ next_phase: v ?? undefined })}
          />
        </label>
        <label className="block">
          <span className="text-[var(--fg-muted)]">set status</span>
          <SelectField
            ariaLabel={`${label} set status`}
            value={setStatus}
            options={statuses}
            allowEmpty="(keep)"
            onCommit={(v) => onChange({ set_status: v ?? undefined })}
          />
        </label>
      </div>
      <label className="flex items-center gap-1.5">
        <input
          type="checkbox"
          aria-label={`${label} requires human approval`}
          checked={requiresApproval}
          onChange={(e) => onChange({ requires_human_approval: e.target.checked })}
          className="h-3.5 w-3.5"
        />
        requires human approval before the move is applied
      </label>
    </>
  );
}

export function TransitionList({
  title,
  edges,
  direction,
}: {
  title: string;
  edges: GraphEdge[];
  direction: "in" | "out";
}) {
  return (
    <div className="mt-2">
      <div className="text-xs text-[var(--fg-muted)] mb-1">{title}</div>
      {edges.length === 0 ? (
        <p className="text-sm text-[var(--fg-muted)]">
          {direction === "out" ? "Nothing — prospects stay here." : "Nothing points here."}
        </p>
      ) : (
        <ul className="space-y-1">
          {edges.map((edge, i) => (
            <li
              key={`${edge.from}-${edge.to}-${i}`}
              className={cn(
                "rounded border px-2 py-1 text-xs",
                edge.dangling ? "border-red-300 bg-red-50" : "border-[var(--border)]",
              )}
            >
              <div className="flex flex-wrap items-center gap-1">
                <span className="font-medium">{edge.label}</span>
                <span className="text-[var(--fg-muted)]">
                  {direction === "out" ? `→ ${edge.to ?? "(no target)"}` : `← ${edge.from}`}
                </span>
                {edge.requiresHumanApproval ? (
                  <span className="rounded border border-amber-300 bg-amber-50 px-1 text-[10px] text-amber-700">
                    needs approval
                  </span>
                ) : null}
                {edge.setStatus ? (
                  <span className="rounded border border-[var(--border)] px-1 text-[10px] text-[var(--fg-muted)]">
                    sets {edge.setStatus}
                  </span>
                ) : null}
              </div>
              {edge.description ? (
                <div className="mt-0.5 text-[var(--fg-muted)]">{edge.description}</div>
              ) : null}
              {edge.dangling ? (
                <div className="mt-0.5 text-red-700">
                  Target phase does not exist in this strategy.
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
